<script setup>

    import { ref, watch, computed, nextTick, onMounted, onBeforeUnmount } from 'vue'
    import { storeToRefs } from 'pinia'
    import { MagnifyingGlassIcon } from '@heroicons/vue/24/solid'
    import { ExclamationCircleIcon, XCircleIcon } from '@heroicons/vue/20/solid'
    import PrimaryButton from '@/Components/Forms/PrimaryButton.vue'
    import { deepCopy } from '@/Helpers'
    import { useClientSingleStore } from '@/Stores'


    const emit = defineEmits(['update:modelValue', 'close'])

    const props = defineProps({
        criterias: {
            type: Array,
            required: true,
        },
        categories: {
            type: Array,
            required: true,
        },
        modelValue: {
            type: Array,
            required: true,
        },
        medicalChartSelectable: {
            type: Boolean,
            default: true
        },
        mode: {
            type: String,
            validator: (value) => ['search', 'client'].includes(value),
            default: 'client'
        }
    })

    const clientSingleStore = useClientSingleStore()
    const { loading } = storeToRefs(clientSingleStore)

    const input = ref(null)
    const searchInput = ref(null)
    const filterCategories = ref([])
    const categoryRefs = ref({})
    const criteriaRefs = ref({})
    const mainPanel = ref(null)
    const unsavedChanges = ref({})

    function clearSearch() {
        input.value = null
        searchInput.value.focus()
    }

    function isMustHaveCheckboxVisible(criteria) {
        if (props.mode === 'search') return criteria.is_must_have_searchable
        return criteria.is_must_have_searchable && criteria.client_input_must_have_selectable !== false
    }

    function setCriteriaRef(name_id, el) {
        if (el) {
            criteriaRefs.value[name_id] = el
        }
    }

    function setCategoryRef(id, el) {
        if (el) {
            categoryRefs.value[id] = el
        }
    }

    function scrollToFirstUnsaved() {
        const firstUnsavedNameId = Object.keys(unsavedChanges.value)[0]
        if (!localCriteriaAttribute.value[firstUnsavedNameId].match_search) {
            clearSearch()
        }
        scrollToCriteria(firstUnsavedNameId)
    }

    function scrollToCriteria(criteriaNameId) {
        nextTick(() => {
            if (criteriaNameId && criteriaRefs.value[criteriaNameId]) {
                const el = criteriaRefs.value[criteriaNameId]
                if (el && mainPanel.value) {
                    const panelTop = mainPanel.value.getBoundingClientRect().top
                    const elTop = el.getBoundingClientRect().top
                    mainPanel.value.scrollBy({
                        top: elTop - panelTop - 40,
                        behavior: 'smooth'
                    })
                }
            }
        })
    }

    function scrollToCategory(categoryId, match_search) {
        if (!match_search) {
            return;
        }

        const el = categoryRefs.value[categoryId]
        if (el && mainPanel.value) {
            const panelTop = mainPanel.value.getBoundingClientRect().top
            const elTop = el.getBoundingClientRect().top
            mainPanel.value.scrollBy({
                top: elTop - panelTop - 40,
                behavior: 'smooth'
            })
        }
    }

    function handleSelectCriteria(criteriaNameId, factor = null, event) {
        if (localCriteriaAttribute.value[criteriaNameId]['selected']) {
            if (localCriteriaAttribute.value[criteriaNameId]['factor'] === factor && factor != null) {
                localCriteriaAttribute.value[criteriaNameId]['factor'] = null
                if (!criteriaNameDict.value[criteriaNameId].is_medical_chartable) {
                    localCriteriaAttribute.value[criteriaNameId]['selected'] = false
                }
            } else {
                localCriteriaAttribute.value[criteriaNameId]['factor'] = factor
                localCriteriaAttribute.value[criteriaNameId]['selected'] = factor == null ? false : true
            }
        } else {
            localCriteriaAttribute.value[criteriaNameId]['selected'] = true
            localCriteriaAttribute.value[criteriaNameId]['factor'] = factor
        }
    }

    function handleRowLabelAreaClick(criteria, event) {
        const currentState = getCurrentState(criteria);
        const nextState = getNextState(currentState, criteria);
        handleSelectCriteria(criteria.name_id, nextState, event);
    }

    function getCurrentState(criteria) {
        const criteriaState = localCriteriaAttribute.value[criteria.name_id];

        if (!criteriaState.selected) {
            return 'none';
        }
        if (!criteriaState.factor && criteriaState.selected) {
            return 'medical_chartable_only';
        }
        return criteriaState.factor;
    }

    function getNextState(currentState, criteria) {
        const stateTransitions = {
            'none': () => {
                if (!props.medicalChartSelectable) {
                    if (criteria.is_must_have_searchable) return 'must_have';
                    if (criteria.is_wow_searchable) return 'wow';
                    return null;
                }
                if (criteria.is_medical_chartable) return null;
                if (props.mode === 'client' && criteria.client_input_must_have_selectable === false) {
                    if (criteria.is_wow_searchable) return 'wow';
                    return null;
                }
                if (criteria.is_must_have_searchable || criteria.is_wow_searchable) return 'must_have';
                return null;
            },
            'medical_chartable_only': () => {
                if (props.mode === 'client' && criteria.client_input_must_have_selectable === false) {
                    if (criteria.is_wow_searchable) return 'wow';
                    return null;
                }
                if (criteria.is_must_have_searchable) return 'must_have';
                if (criteria.is_wow_searchable) return 'wow';
                return null;
            },
            'must_have': () => {
                if (criteria.is_wow_searchable) return 'wow';
                return null;
            },
            'wow': () => null
        };

        const transition = stateTransitions[currentState];
        return transition ? transition() : null;
    }

    function handleSelectCriteriaAttributeMultiple(criteriaNameId, attributeNameId, choiceId) {
        if (localCriteriaAttribute.value[criteriaNameId]['attributes'][attributeNameId].includes(choiceId)) {
            localCriteriaAttribute.value[criteriaNameId]['attributes'][attributeNameId] = localCriteriaAttribute.value[criteriaNameId]['attributes'][attributeNameId].filter(item => item !== choiceId)
        } else {
            localCriteriaAttribute.value[criteriaNameId]['attributes'][attributeNameId].push(choiceId)
        }
    }

    function handleDone() {
        if (Object.keys(unsavedChanges.value).length > 0) {
            scrollToFirstUnsaved()
        } else {
            emit('close')
        }
    }

    const criteriaNameDict = computed(() => {
        const dict = {}
        props.criterias.forEach(criteria => {
            dict[criteria.name_id] = deepCopy(criteria)
            dict[criteria.name_id]['attribute_dict'] = {}
            criteria.attributes.forEach(attribute => {
                dict[criteria.name_id]['attribute_dict'][attribute.name_id] = attribute
            })
        })
        return dict;
    })

    const localCriteriaAttribute = ref({})

    const selectedSummary = computed(() => {
        const data = {
            is_medical_chartable : 0,
            must_have : 0,
            wow : 0,
            unsaved : Object.keys(unsavedChanges.value).length
        }

        Object.keys(localCriteriaAttribute.value).forEach(criteriaNameId => {
            const criteria = criteriaNameDict.value[criteriaNameId]
            const localCriteria = localCriteriaAttribute.value[criteriaNameId]

            if (localCriteria.selected) {
                if (criteria.is_medical_chartable) {
                    data.is_medical_chartable++
                }
                if (localCriteria.factor === 'must_have') {
                    data.must_have++
                }
                if (localCriteria.factor === 'wow') {
                    data.wow++
                }
            }
        })

        return data;
    })

    watch([() => props.criterias, () => props.modelValue], ([criterias, selected]) => {
        localCriteriaAttribute.value = {}

        criterias.forEach(criteria => {
            localCriteriaAttribute.value[criteria.name_id] = {attributes: {}}
            if (unsavedChanges.value[criteria.name_id]) {
                localCriteriaAttribute.value[criteria.name_id]['factor'] = unsavedChanges.value[criteria.name_id].factor
                localCriteriaAttribute.value[criteria.name_id]['selected'] = true
            }

            criteria.attributes.forEach(attributeDescriptor => {
                let defaultChoices = []
                if (attributeDescriptor.can_client_select_multiple == false) {
                    defaultChoices = null
                }
                localCriteriaAttribute.value[criteria.name_id]['attributes'][attributeDescriptor.name_id] = defaultChoices

                if (unsavedChanges.value[criteria.name_id] && unsavedChanges.value[criteria.name_id].attributes) {
                    unsavedChanges.value[criteria.name_id].attributes.forEach(unSavedAttribute => {
                        if (unSavedAttribute.name_id === attributeDescriptor.name_id) {
                            localCriteriaAttribute.value[criteria.name_id]['attributes'][attributeDescriptor.name_id] = attributeDescriptor.can_client_select_multiple == true ? unSavedAttribute.selected_choice_ids : unSavedAttribute.selected_choice_ids[0]
                        }
                    })
                }
            })
        })

        selected.forEach(criteria => {
            localCriteriaAttribute.value[criteria.name_id]['factor'] = criteria.factor
            localCriteriaAttribute.value[criteria.name_id]['selected'] = true
            criteria.attributes.forEach(attribute => {
                const attributeDescriptor = criteriaNameDict.value[criteria.name_id].attribute_dict[attribute.name_id]
                if (attributeDescriptor.can_client_select_multiple === false) {
                    localCriteriaAttribute.value[criteria.name_id]['attributes'][attribute.name_id] = attribute.selected_choice_ids.length > 0 ? attribute.selected_choice_ids[0] : null
                } else {
                    localCriteriaAttribute.value[criteria.name_id]['attributes'][attribute.name_id] = attribute.selected_choice_ids
                }
            })
        })
    }, { immediate: true })

    watch([() => props.categories, () => props.criterias], ([categories, criterias]) => {
        const categoryCriteriaDict = {}
        categories.forEach(category => {
            categoryCriteriaDict[category.id] = []
        })

        criterias.forEach(criteria => {
            if (categoryCriteriaDict[criteria.category_id]) {
                categoryCriteriaDict[criteria.category_id].push(criteria)
            }
        })

        let tableIndex = 0
        filterCategories.value = categories.map((category) => {
            return {
                ...category,
                tableIndex: tableIndex++,
                criterias: categoryCriteriaDict[category.id]
                    .filter(criteria => {
                        if (props.medicalChartSelectable) {
                            return true;
                        }
                        return criteria.is_must_have_searchable || criteria.is_wow_searchable;
                    })
                    .map((criteria, index) => {
                        return {
                            ...criteria,
                            tableIndex: tableIndex++
                        };
                    })
            };
        })
    }, { immediate: true })

    watch(localCriteriaAttribute, (newValue) => {
        const selected = []
        Object.keys(newValue).forEach(criteriaNameId => {
            const criteria = newValue[criteriaNameId]
            if (!criteria.selected) {
                delete unsavedChanges.value[criteriaNameId]
                return;
            }

            const missingRequiredAttributes = Object.keys(criteria.attributes).filter(attributeNameId => {
                const attributeDescriptor = criteriaNameDict.value[criteriaNameId].attribute_dict[attributeNameId]
                if (attributeDescriptor.required_client == true && (localCriteriaAttribute.value[criteriaNameId]['attributes'][attributeNameId] == null || localCriteriaAttribute.value[criteriaNameId]['attributes'][attributeNameId] == '')) {
                    return true;
                }
                return false;
            })

            const criteriaSummary = {
                name_id: criteriaNameId,
                factor: criteria.factor,
                attributes: Object.keys(criteria.attributes).map(attributeNameId => {
                    let selectedChoiceIds = criteria.attributes[attributeNameId]
                    selectedChoiceIds = Array.isArray(selectedChoiceIds) ? selectedChoiceIds : ((selectedChoiceIds != '' && selectedChoiceIds != null) ? [selectedChoiceIds] : [])

                    return {
                        name_id: attributeNameId,
                        selected_choice_ids: selectedChoiceIds
                    };
                })
            }

            if (missingRequiredAttributes.length > 0) {
                unsavedChanges.value[criteriaNameId] = criteriaSummary
            } else {
                delete unsavedChanges.value[criteriaNameId]
                selected.push(criteriaSummary)
            }
        })

        if (hasChange(selected, props.modelValue) == false) {
            return;
        }
        emit('update:modelValue', selected)
    }, { immediate: true, deep: true })

    function hasChange(a, b) {
        const newValue = deepCopy(a)
        const oldValue = deepCopy(b)

        newValue.sort((a, b) => a.name_id.localeCompare(b.name_id))
        oldValue.sort((a, b) => a.name_id.localeCompare(b.name_id))
        if (newValue.length !== oldValue.length) {
            return true;
        }
        for (let i = 0; i < newValue.length; i++) {
            if (newValue[i].name_id !== oldValue[i].name_id) {
                return true;
            }
            if (newValue[i].factor !== oldValue[i].factor) {
                return true;
            }
            if (newValue[i].attributes.length !== oldValue[i].attributes.length) {
                return true;
            }
            newValue[i].attributes.sort((a, b) => a.name_id.localeCompare(b.name_id))
            oldValue[i].attributes.sort((a, b) => a.name_id.localeCompare(b.name_id))
            for (let j = 0; j < newValue[i].attributes.length; j++) {
                if (newValue[i].attributes[j].name_id !== oldValue[i].attributes[j].name_id) {
                    return true;
                }
                if (JSON.stringify(newValue[i].attributes[j].selected_choice_ids) !== JSON.stringify(oldValue[i].attributes[j].selected_choice_ids)) {
                    return true;
                }
            }
        }
        return false;
    }

    watch(input, (newValue) => {
        if (newValue != null && newValue.trim() !== '') {
            const searchString = newValue.trim().toLowerCase()
            Object.keys(filterCategories.value).forEach(category => {
                let categoryMatch = false
                filterCategories.value[category].criterias.forEach(criteria => {
                    criteria.match_search = criteria.label.toLowerCase().includes(searchString)
                    criteria.attributes.forEach(attribute => {
                        attribute.match_search = attribute.description_label.toLowerCase().includes(searchString)
                        if (attribute.match_search) {
                            criteria.match_search = true
                        }

                        attribute.choices.forEach(choice => {
                            choice.match_search = choice.label.toLowerCase().includes(searchString)
                            if (choice.match_search) {
                                criteria.match_search = true
                            }
                        })

                        if (criteria.match_search) {
                            criteria.match_search = true
                        }
                    })

                    if (criteria.match_search) {
                        categoryMatch = true
                    }
                })
                filterCategories.value[category].match_search = categoryMatch
            })
        } else {
            Object.keys(filterCategories.value).forEach(category => {
                filterCategories.value[category].match_search = true
                filterCategories.value[category].criterias.forEach(criteria => {
                    criteria.match_search = true
                })
            })
        }
    }, {immediate: true})

    function handleBeforeUnload(event) {
        if (unsavedChanges.value && Object.keys(unsavedChanges.value).length > 0) {
            window.confirm('You have unsaved changes. Are you sure you want to leave?')
            scrollToFirstUnsaved()
            event.returnValue = ''
            event.preventDefault()
        }
    }

    onMounted(() => {
        window.addEventListener('beforeunload', handleBeforeUnload);
    })

    onBeforeUnmount(() => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
    })

    defineExpose({
        scrollToCriteria
    })

</script>

<template>

    <div class="flex w-full flex-col rounded-lg">
        <div class="grid grid-cols-8">

            <div class="col-span-2 bg-lifeworx-blue-600 bg-opacity-15 border-r border-stone-300 py-5 px-3">
                <span class="inline-block italic font-semibold text-xs text-lifeworx-blue-600 ml-2 mb-2">Jump to...</span>
                <ul class="ml-5">
                    <li
                        v-for="category in filterCategories"
                        :key="category.id"
                        :class="[
                            'py-2',
                            {
                                'cursor-pointer text-lifeworx-blue-800 hover:text-lifeworx-blue-600' : category.match_search === true,
                                'cursor-default text-stone-400' : category.match_search === false,
                            }
                        ]"
                        @click="scrollToCategory(category.id, category.match_search)"
                    >
                        <span
                            class="text-sm font-semibold"
                        >
                            {{ category.name }}
                        </span>
                    </li>
                </ul>
            </div>

            <div class="col-span-6">
                <div class="flex p-3 rounded-tr-lg focus-within:ring-lifeworx-blue-500 focus-within:ring-1 focus-within:ring-inset border-b border-stone-300">
                    <MagnifyingGlassIcon class="flex-none inline-flex h-6 w-6 text-stone-400" />
                    <input
                        v-model="input"
                        class="border-0 h-6 flex-grow w-full px-2.5 py-1.5 text-sm text-stone-700 placeholder:text-stone-400 focus:ring-0 bg-transparent"
                        id="search"
                        ref="searchInput"
                        placeholder="Start typing to search..."
                        type="text"
                        autocomplete="off"
                    />
                    <button
                        v-if="input"
                        type="button"
                        class="text-stone-400 hover:text-lifeworx-red-500 focus:outline-none focus:ring-0"
                        @click="clearSearch()"
                    >
                            <XCircleIcon class="flex-none inline-flex h-6 w-6 -mt-1.5 -mb-1" />
                    </button>


                </div>
                <div
                    ref="mainPanel"
                    class="h-[32rem] overflow-y-scroll block"
                >
                    <table class="w-full">
                        <thead class="bg-white sticky top-0 shadow-sm cursor-default z-[8000]">
                            <tr>
                                <td class="py-2 px-8">
                                    <span class="font-bold text-xs text-stone-400">Conditions &amp; Criteria</span>
                                </td>
                                <td v-if="props.medicalChartSelectable" class="w-28 text-center py-2 text-xs font-bold text-lifeworx-red-500">Medical Chart</td>
                                <td class="w-28 text-center py-2 text-xs font-bold text-lifeworx-orange-500">Must-Have</td>
                                <td class="w-28 text-center py-2 text-xs font-bold text-lifeworx-purple-400">Wow-Factor</td>
                            </tr>
                        </thead>
                        <tbody>
                            <template
                                v-for="(category, categoryIndex) in filterCategories"
                                :key="category.id"
                            >
                                <tr
                                    v-if="category.match_search === true"
                                    :ref="el => setCategoryRef(category.id, el)"
                                    :class="[
                                        'border-b cursor-default text-lifeworx-blue-900 group',
                                        {
                                            'bg-stone-100' : category.tableIndex % 2 === 0
                                        }
                                    ]"
                                >
                                    <td class="text-md pl-4 py-3 font-bold text-lifeworx-blue-600" colspan="4">{{ category.name }}</td>
                                </tr>
                                <template
                                    v-for="criteria in category.criterias"
                                    :key="`${category.id}-${criteria.id}`"
                                >
                                    <tr
                                        v-if="criteria.match_search === true"
                                        :ref="el => setCriteriaRef(criteria.name_id, el)"
                                        :class="[
                                            {
                                                'bg-stone-100 hover:bg-lifeworx-blue-600 hover:bg-opacity-15' : criteria.tableIndex % 2 === 0,
                                                'hover:bg-lifeworx-blue-600 hover:bg-opacity-15' : criteria.tableIndex % 2 === 1
                                            }
                                        ]"
                                    >
                                        <td colspan="4">
                                            <!-- main row -->
                                            <div class="flex">
                                                <div
                                                    :class="[
                                                        'flex grow items-center'
                                                    ]"
                                                >
                                                    <h4
                                                        @click.stop="(event) => handleRowLabelAreaClick(criteria, event)"
                                                        :class="[
                                                            'hover:font-bold hover:italic hover:tracking-normal text-sm px-8 py-2 cursor-pointer hover:text-lifeworx-blue-600',
                                                            {
                                                                'font-bold' : localCriteriaAttribute[criteria.name_id]['selected'],
                                                                'text-lifeworx-red-500' : (
                                                                    criteria.is_medical_chartable && localCriteriaAttribute[criteria.name_id]['selected'] && localCriteriaAttribute[criteria.name_id]['factor'] === null)
                                                                    || (localCriteriaAttribute[criteria.name_id]['selected'] && unsavedChanges[criteria.name_id] != undefined),
                                                                'text-lifeworx-orange-500' : localCriteriaAttribute[criteria.name_id]['selected'] && localCriteriaAttribute[criteria.name_id]['factor'] === 'must_have',
                                                                'text-lifeworx-purple-400' : localCriteriaAttribute[criteria.name_id]['selected'] && localCriteriaAttribute[criteria.name_id]['factor'] === 'wow',
                                                            }
                                                        ]"
                                                    >
                                                        <span
                                                            :class="[
                                                                'hover:text-lifeworx-blue-600 select-none',
                                                                {
                                                                    'italic border-b border-dotted border-lifeworx-red-500' : localCriteriaAttribute[criteria.name_id]['selected'] && unsavedChanges[criteria.name_id] != undefined
                                                            }]"
                                                        >
                                                            {{ criteria.label }}
                                                        </span>

                                                        <ExclamationCircleIcon
                                                            v-if="localCriteriaAttribute[criteria.name_id].selected && unsavedChanges[criteria.name_id] != undefined"
                                                            class="inline-flex h-5 w-5 text-lifeworx-red-500 -mt-0.5 ml-1"
                                                        />

                                                    </h4>
                                                </div>
                                                <div
                                                    v-if="props.medicalChartSelectable"
                                                    @click.stop="(event) => criteria.is_medical_chartable && handleSelectCriteria(criteria.name_id, null, event)"
                                                    :class="[
                                                        'flex w-28 items-center justify-center text-center py-1',
                                                        {
                                                            'cursor-pointer hover:bg-gradient-to-r hover:from-transparent hover:via-lifeworx-red-600/20 hover:to-transparent group': criteria.is_medical_chartable
                                                        }
                                                    ]"
                                                >
                                                    <input
                                                        v-if="criteria.is_medical_chartable"
                                                        type="checkbox"
                                                        :checked="localCriteriaAttribute[criteria.name_id]['selected']"
                                                        @click.stop="(event) => handleSelectCriteria(criteria.name_id, null, event)"
                                                        :class="[
                                                            'cursor-pointer m-2 rounded-sm focus:ring-0 text-lifeworx-red-500 focus:bg-lifeworx-red-500 focus:bg-opacity-50 checked:bg-lifeworx-red-500',
                                                            {
                                                                'group-hover:bg-lifeworx-red-500/25 group-hover:border-lifeworx-red-500 group-hover:backdrop-blur group-hover:backdrop-brightness-200' : !localCriteriaAttribute[criteria.name_id]['selected']
                                                            }
                                                        ]"
                                                    />
                                                </div>
                                                <div
                                                    @click.stop="(event) => criteria.is_must_have_searchable && isMustHaveCheckboxVisible(criteria) && handleSelectCriteria(criteria.name_id, 'must_have', event)"
                                                    :class="[
                                                        'flex w-28 items-center justify-center text-center py-1',
                                                    {
                                                        'cursor-pointer hover:bg-gradient-to-r hover:from-transparent hover:via-lifeworx-orange-600/20 hover:to-transparent group' : criteria.is_must_have_searchable && isMustHaveCheckboxVisible(criteria)
                                                    }
                                                ]"
                                            >
                                                    <input
                                                        v-if="isMustHaveCheckboxVisible(criteria)"
                                                        type="checkbox"
                                                        :checked="localCriteriaAttribute[criteria.name_id].factor === 'must_have'"
                                                        @click.stop="(event) => handleSelectCriteria(criteria.name_id, 'must_have', event)"
                                                        :class="[
                                                            'cursor-pointer m-2 rounded-sm focus:ring-0 text-lifeworx-orange-500 focus:bg-lifeworx-orange-500 focus:bg-opacity-50 checked:bg-lifeworx-orange-500 hover:bg-lifeworx-orange-500/25 hover:border-lifeworx-orange-500 disabled:bg-stone-100 disabled:border-stone-300 disabled:cursor-not-allowed',
                                                            {
                                                                'cursor-pointer' : localCriteriaAttribute[criteria.name_id].factor == 'must_have',
                                                                'group-hover:bg-lifeworx-orange-500/25 group-hover:border-lifeworx-orange-500 group-hover:backdrop-blur group-hover:backdrop-brightness-200' : localCriteriaAttribute[criteria.name_id].factor !== 'must_have'
                                                            }
                                                        ]"
                                                    />
                                                </div>
                                                <div
                                                    @click.stop="(event) => criteria.is_wow_searchable && handleSelectCriteria(criteria.name_id, 'wow', event)"
                                                    :class="[
                                                        'flex w-28 items-center justify-center text-center py-1',
                                                        {
                                                            'cursor-pointer hover:bg-gradient-to-r hover:from-transparent hover:via-lifeworx-purple-600/20 hover:to-transparent group' : criteria.is_wow_searchable
                                                        }
                                                    ]"
                                                >
                                                    <input
                                                        v-if="criteria.is_wow_searchable"
                                                        type="checkbox"
                                                        :checked="localCriteriaAttribute[criteria.name_id].factor === 'wow'"
                                                        @click.stop="(event) => handleSelectCriteria(criteria.name_id, 'wow', event)"
                                                        :class="[
                                                            'cursor-pointer m-2 rounded-sm focus:ring-0 text-lifeworx-purple-400 focus:bg-lifeworx-purple-400 focus:bg-opacity-50 checked:bg-lifeworx-purple-400 hover:bg-lifeworx-purple-400/25 hover:border-lifeworx-purple-400 disabled:bg-stone-100 disabled:border-stone-300 disabled:cursor-not-allowed',
                                                            {
                                                                'group-hover:bg-lifeworx-purple-400/25 group-hover:border-lifeworx-purple-400 group-hover:backdrop-blur group-hover:backdrop-brightness-200' : localCriteriaAttribute[criteria.name_id].factor !== 'wow',
                                                            }
                                                        ]"
                                                    />
                                                </div>
                                            </div>
                                            <!-- end main row -->

                                            <div
                                                v-if="criteria.match_search === true && criteria.attributes.length && localCriteriaAttribute[criteria.name_id]['selected']"
                                                @click.stop="(event) => handleRowLabelAreaClick(criteria, event)"
                                            >
                                                <div
                                                    @click.stop
                                                    :class="[
                                                        'flex gap-4 ml-8 px-3 py-3 rounded-md mr-5 mb-3 border cursor-default',
                                                        {
                                                            'bg-stone-100 border-lifeworx-red-500 border-opacity-50' : unsavedChanges[criteria.name_id] == undefined,
                                                            'bg-[#eed3cc] border-lifeworx-red-500 border-dashed' : unsavedChanges[criteria.name_id] != undefined,
                                                            'border-lifeworx-orange-600' : localCriteriaAttribute[criteria.name_id].factor === 'must_have',
                                                            'border-lifeworx-purple-400' : localCriteriaAttribute[criteria.name_id].factor === 'wow',
                                                        }
                                                    ]"
                                                >
                                                    <template
                                                        v-for="(attribute, i) in criteria.attributes"
                                                        :key="attribute.name_id"
                                                    >
                                                        <div
                                                            v-if="attribute.can_client_select_multiple === false"
                                                            class="flex flex-col gap-2"
                                                        >
                                                            <label
                                                                :for="`${criteria.name_id}-${attribute.name_id}`"
                                                                class="text-xs text-lifeworx-blue-800"
                                                            >
                                                                <template v-if="attribute.required_client === true">
                                                                    {{ attribute.description_label == "" ? "Select one" : attribute.description_label }}
                                                                </template>
                                                                <template v-else>
                                                                    {{ attribute.description_label == "" ? "Optional" : attribute.description_label }}
                                                                </template>
                                                            </label>
                                                            <select
                                                                v-model="localCriteriaAttribute[criteria.name_id]['attributes'][attribute.name_id]"
                                                                :id="`${criteria.name_id}-${attribute.name_id}`"
                                                                class="text-xs relative w-full min-w-40 pl-2.5 h-[34px] pr-10 py-7 md:py-1.5 rounded-md tracking-wider border border-stone-400 shadow-sm focus:border-lifeworx-blue-600 focus:ring-lifeworx-blue-600 focus:ring-1 focus:outline-none text-lifeworx-blue-700"
                                                            >
                                                                <option
                                                                    v-if="attribute.required_client === false"
                                                                    :value="null"
                                                                >
                                                                    -- None --
                                                                </option>
                                                                <option
                                                                    v-for="(choice, i) in attribute.choices"
                                                                    :key="`${criteria.id}-${attribute.id}-${i}`"
                                                                    :value="choice.id"
                                                                >
                                                                    {{ choice.label }}
                                                                </option>
                                                            </select>
                                                        </div>

                                                        <div
                                                            v-if="attribute.can_client_select_multiple === true"
                                                            class="flex flex-col gap-2"
                                                        >
                                                            <label
                                                                class="text-xs text-lifeworx-blue-800"
                                                            >
                                                                <template v-if="attribute.required_client === true">
                                                                    {{ attribute.description_label == "" ? "Select at least one" : attribute.description_label }}
                                                                </template>
                                                                <template v-else>
                                                                    {{ attribute.description_label == "" ? "Optional" : attribute.description_label }}
                                                                </template>
                                                            </label>
                                                            <ul>
                                                                <template
                                                                    v-for="(choice, i) in attribute.choices"
                                                                    :key="`${criteria.id}-${attribute.id}-${i}`"
                                                                >
                                                                    <li class="mr-4 float-left mb-2">
                                                                        <div class="flex items-center gap-2">
                                                                            <input
                                                                                :id="`${criteria.name_id}_${attribute.name_id}_${choice.id}`"
                                                                                type="checkbox"
                                                                                :value="choice.id"
                                                                                :checked="localCriteriaAttribute[criteria.name_id]['attributes'][attribute.name_id].includes(choice.id)"
                                                                                @click.stop="() => handleSelectCriteriaAttributeMultiple(criteria.name_id, attribute.name_id, choice.id)"
                                                                                class="focus:ring-0 text-lifeworx-blue-600 focus:bg-lifeworx-blue-600 focus:bg-opacity-50 checked:bg-lifeworx-blue-600 rounded-sm cursor-pointer"
                                                                            />
                                                                            <label
                                                                                class="text-xs cursor-pointer"
                                                                                :for="`${criteria.name_id}_${attribute.name_id}_${choice.id}`"
                                                                            >
                                                                                {{ choice.label }}
                                                                            </label>
                                                                        </div>
                                                                    </li>
                                                                </template>
                                                            </ul>
                                                        </div>

                                                    </template>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                            </template>

                        </tbody>
                    </table>
                </div>

            </div>
        </div>
        <div class="bg-white flex justify-between border-t border-stone-300 px-6 py-3">
            <div class="grid content-center">
                <span
                    v-if="loading"
                    class="inline-block h-full text-sm font-semibold italic"
                >
                    Saving...
                </span>
                <span
                    v-else-if="(!props.medicalChartSelectable || !selectedSummary.is_medical_chartable) && !selectedSummary.must_have && !selectedSummary.wow"
                    class="inline-block h-full text-sm"
                >
                    No Criteria Selected
                </span>
                <span
                    v-else-if="selectedSummary.is_medical_chartable || selectedSummary.must_have || selectedSummary.wow"
                    class="inline-block h-full text-sm"
                >
                    Selected
                    <template v-if="props.medicalChartSelectable">
                        <span v-if="selectedSummary.is_medical_chartable" class="text-lifeworx-red-500 font-bold">{{ selectedSummary.is_medical_chartable }} {{ selectedSummary.is_medical_chartable > 1 ? 'conditions/devices' : 'condition/device'}}</span>
                        <template v-if="selectedSummary.is_medical_chartable && selectedSummary.must_have && selectedSummary.wow">, </template>
                        <template v-if="selectedSummary.is_medical_chartable && selectedSummary.must_have && !selectedSummary.wow"> and </template>
                    </template>
                    <span v-if="selectedSummary.must_have" class="text-lifeworx-orange-500 font-bold">{{ selectedSummary.must_have }} {{ selectedSummary.must_have > 1 ? 'must-haves' : 'must-have'}}</span>
                    <template v-if="selectedSummary.must_have && selectedSummary.wow"> and </template>
                    <span v-if="selectedSummary.wow" class="text-lifeworx-purple-400 font-bold">{{ selectedSummary.wow }} {{ selectedSummary.wow > 1 ? 'wow-factors' : 'wow-factor'}}</span>

                    <template v-if="selectedSummary.unsaved"> &mdash; includes </template>

                    <button
                        v-if="selectedSummary.unsaved"
                        @click="scrollToFirstUnsaved()"
                        class="text-lifeworx-red-500 font-bold italic"
                    >
                        <span class="border-b border-dotted border-lifeworx-red-500 hover:border-solid">{{ selectedSummary.unsaved }} incomplete criteria</span>
                        <ExclamationCircleIcon
                            class="inline-flex h-5 w-5 -mt-1 ml-1"
                        />
                    </button>

                </span>
            </div>
            <PrimaryButton @click="handleDone">Done</PrimaryButton>
        </div>
    </div>
</template>
